import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import NeuroRadarChart from './NeuroRadarChart';
import neuroService from '../../services/neuro.service';
import whatsappService from '../../services/whatsapp.service';

const NeuroResultsPanel = ({
  result,
  evaluationId,
  kid = null,
  showTitle = true,
  compact = false,
  showHomeButton = false,
  showRetake = false,
  showPdfDownload = false,
  showEmailSend = false,
  onRetake,
}) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [emailSent, setEmailSent] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, success: true, message: '' });

  if (!result) return null;

  const evalId = evaluationId || result.evaluation_id;
  const hasMatchedRule = Boolean(result.matched_rule);

  const referenceCode = result.reference_code || result.anonymous_code;
  const kidFirstname = kid?.firstname || result.kid_firstname || null;
  const kidName = kid?.name || result.kid_name || null;

  const handleNeuroSupport = async (intent = 'accompagnement') => {
    try {
      await whatsappService.openWhatsAppNeuroSupport({
        intent,
        user,
        kidFirstname,
        kidName,
        referenceCode,
        profileTitle: result.profile_title || result.matched_rule?.name,
        dominantInsight: result.dominant_insight,
        scores: result.scores,
        spectra: result.spectra,
        axisLabels: result.axis_labels,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownloadPdf = async () => {
    if (!evalId) return;
    try {
      const blob = await neuroService.downloadPdf(evalId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan-neuro-${referenceCode || evalId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSendEmail = async () => {
    if (!evalId) return;
    try {
      await neuroService.sendResultsEmail(evalId);
      setEmailSent(true);
      setEmailDialog({
        open: true,
        success: true,
        message: 'Les résultats ont été envoyés à votre adresse email.',
      });
    } catch (e) {
      console.error(e);
      setEmailDialog({
        open: true,
        success: false,
        message: 'Impossible d\'envoyer l\'email. Veuillez réessayer plus tard.',
      });
    }
  };

  const closeEmailDialog = () => setEmailDialog((prev) => ({ ...prev, open: false }));

  return (
    <Box>
      {showTitle && (
        <>
          <Typography
            variant={compact ? 'h6' : 'h5'}
            sx={{ color: '#784e3c', fontWeight: 700, mb: 1, textAlign: compact ? 'left' : 'center' }}
          >
            Profil neuroatypique
          </Typography>
          {referenceCode && (
            <Typography variant="caption" sx={{ display: 'block', color: '#888', mb: 2, textAlign: compact ? 'left' : 'center' }}>
              Réf. {referenceCode}
            </Typography>
          )}
        </>
      )}

      {(result.profile_intro || result.profile_title) && (
        <Box
          sx={{
            bgcolor: '#f8f4f0',
            borderLeft: '4px solid #784e3c',
            borderRadius: 2,
            p: compact ? 2 : 3,
            mb: 3,
          }}
        >
          {result.profile_intro && (
            <Typography sx={{ color: '#555', mb: 1, lineHeight: 1.6 }}>
              {result.profile_intro}
            </Typography>
          )}
          {result.profile_title && (
            <Typography
              variant={compact ? 'subtitle1' : 'h6'}
              sx={{ color: '#784e3c', fontWeight: 700, mb: result.dominant_insight ? 1 : 0 }}
            >
              {result.profile_title}
            </Typography>
          )}
          {result.dominant_insight && (
            <Typography variant="body2" sx={{ color: '#777' }}>
              {result.dominant_insight}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: 2,
          p: compact ? 2 : { xs: 2, sm: 3 },
          mb: 3,
          boxShadow: compact ? 0 : 1,
          border: compact ? '1px solid #eee' : 'none',
        }}
      >
        <NeuroRadarChart
          scores={result.scores}
          axisLabels={result.axis_labels}
          spectra={result.spectra}
          compact={compact}
        />
      </Box>

      {result.scores && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: compact ? 'flex-start' : 'center',
            gap: 2,
            mb: 2,
          }}
        >
          {(result.spectra?.length
            ? result.spectra.map((s) => ({ slug: s.slug, label: s.short_label || s.label, max: s.max_score }))
            : Object.entries(result.axis_labels || {}).map(([slug, label]) => ({ slug, label, max: 10 }))
          ).map((axis, i) => (
            <Typography key={axis.slug} variant="body2" sx={{ color: '#555' }}>
              {i > 0 && '· '}{axis.label} : <strong>{result.scores[axis.slug] ?? 0}/{axis.max ?? 10}</strong>
            </Typography>
          ))}
        </Box>
      )}

      {result.restitution_text && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#784e3c', fontWeight: 700, mb: 1 }}
          >
            Recommandation pédagogique
          </Typography>
          <Typography sx={{ color: '#444', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {result.restitution_text}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 3 }}>
        Cet outil d&apos;orientation pédagogique ne constitue pas un diagnostic médical.
      </Typography>

      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: 2,
          border: '1px solid #e8ddd4',
          p: compact ? 2 : 3,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#784e3c', fontWeight: 700, mb: 1 }}>
          Accompagnement personnalisé
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
          Notre équipe vous accompagne pour choisir les solutions les plus adaptées au profil de votre enfant.
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => handleNeuroSupport('accompagnement')}
          sx={{ minHeight: 48, backgroundColor: '#784e3c', mb: 1.5, fontWeight: 'bold' }}
        >
          Nous contacter pour un accompagnement
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={() => handleNeuroSupport('valed_neuro')}
          sx={{ minHeight: 44, color: '#784e3c', fontWeight: 600 }}
        >
          Constituer sa VALED NEURO
        </Button>
        {!hasMatchedRule && (
          <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 1 }}>
            Un conseiller vous orientera vers la solution la plus adaptée.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {showPdfDownload && (
          <Button
            variant="outlined"
            onClick={handleDownloadPdf}
            disabled={!evalId}
            sx={{ minHeight: 48, borderColor: '#784e3c', color: '#784e3c' }}
          >
            Télécharger le PDF anonymisé
          </Button>
        )}
        {showEmailSend && (
          <Button
            variant="text"
            onClick={handleSendEmail}
            disabled={!evalId || emailSent}
            sx={{ minHeight: 48, color: '#784e3c' }}
          >
            {emailSent ? 'Email envoyé' : 'Recevoir par email'}
          </Button>
        )}
        {showRetake && onRetake && (
          <Button variant="text" onClick={onRetake} sx={{ minHeight: 48, color: '#784e3c' }}>
            Refaire l&apos;évaluation
          </Button>
        )}
        {showHomeButton && (
          <Button variant="text" onClick={() => navigate('/HomePage')} sx={{ minHeight: 48, color: '#784e3c' }}>
            Retour à l&apos;accueil
          </Button>
        )}
      </Box>

      <Dialog open={emailDialog.open} onClose={closeEmailDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#784e3c', fontWeight: 'bold' }}>
          {emailDialog.success ? 'Email envoyé' : 'Erreur d\'envoi'}
        </DialogTitle>
        <DialogContent>
          <Typography>{emailDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailDialog} sx={{ color: '#784e3c' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NeuroResultsPanel;
